import { Controller } from "@hotwired/stimulus";

// Fetches bulk match preview when import_member_id changes, updates Apply mapping section,
// and renders the Selected member card (source of truth).
export default class extends Controller {
  static values = {
    previewUrl: String,
    hasCandidates: { type: Boolean, default: false },
  };

  connect() {
    this.boundHandleChange = this.handleChange.bind(this);
    this.boundHandleMemberSelected = this.handleMemberSelected.bind(this);
    this.lastSelectionSource = null;
    this.element.addEventListener("change", this.boundHandleChange);
    this.element.addEventListener("member-selected", this.boundHandleMemberSelected);
    setTimeout(() => this.updateSelectionState(), 0);
  }

  disconnect() {
    this.element.removeEventListener("change", this.boundHandleChange);
    this.element.removeEventListener("member-selected", this.boundHandleMemberSelected);
  }

  handleMemberSelected(e) {
    this.lastSelectionSource = e.detail?.source || "manual";
  }

  handleChange(e) {
    if (e.target.matches && e.target.matches('input[type="hidden"][name*="import_member_id"]')) {
      this.updateSelectionState();
    }
  }

  getHiddenField() {
    return this.element.querySelector('input[type="hidden"][name*="import_member_id"]')
      || document.querySelector('form input[type="hidden"][name*="import_member_id"]');
  }

  getComboboxInput() {
    return this.element.querySelector('[data-forms--combobox-target="input"]');
  }

  updateSelectionState() {
    const hidden = this.getHiddenField();
    const id = hidden?.value?.trim();
    const card = this.element.querySelector("[data-selected-member-card]");
    if (!card) return;

    if (!id) {
      card.classList.add("hidden");
      this.lastSelectionSource = null;
      this.renderPreview({ total_matching: 0, will_update: 0, skipped: 0 });
      return;
    }

    card.classList.remove("hidden");
    const input = this.getComboboxInput();
    const label = input?.value?.trim() || "";
    const source = this.lastSelectionSource || "manual";
    this.lastSelectionSource = null;

    const nameEl = this.element.querySelector("[data-selected-member-name]");
    const detailsEl = this.element.querySelector("[data-selected-member-details]");
    const badgeEl = this.element.querySelector("[data-selected-member-badge]");
    const overrideEl = this.element.querySelector("[data-selected-member-override]");

    if (nameEl) nameEl.textContent = label.split(" · ")[0] || label || "—";
    if (detailsEl) detailsEl.textContent = label.includes(" · ") ? label.split(" · ").slice(1).join(" · ") : (label.includes("@") ? label : "—");
    if (badgeEl) badgeEl.textContent = source === "suggestion" ? "Suggested Match" : "Manual Selection";
    if (overrideEl) {
      if (source === "manual" && this.hasCandidatesValue) {
        overrideEl.classList.remove("hidden");
        overrideEl.textContent = `You selected ${label.split(" · ")[0] || "this member"} manually. This overrides the suggested match.`;
      } else {
        overrideEl.classList.add("hidden");
        overrideEl.textContent = "";
      }
    }

    this.fetchPreview();
  }

  async fetchPreview() {
    const hidden = this.getHiddenField();
    const id = hidden?.value?.trim();
    if (!id || !this.previewUrlValue) {
      this.renderPreview({ total_matching: 0, will_update: 0, skipped: 0 });
      return;
    }

    try {
      const url = new URL(this.previewUrlValue, window.location.origin);
      url.searchParams.set("import_member_id", id);
      const res = await fetch(url.toString(), {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const data = await res.json();
      this.renderPreview(data);
    } catch {
      this.renderPreview({ total_matching: 0, will_update: 0, skipped: 0 });
    }
  }

  updateSelectedMemberFromPreview(data) {
    const name = data?.selected_member_name;
    const card = this.element.querySelector("[data-selected-member-card]");
    if (!card || card.classList.contains("hidden")) return;

    const nameEl = this.element.querySelector("[data-selected-member-name]");
    const detailsEl = this.element.querySelector("[data-selected-member-details]");
    if (name && nameEl) nameEl.textContent = name;
    if (detailsEl) {
      const detailsParts = [ data?.selected_member_email, data?.selected_member_phone ].filter(Boolean);
      detailsEl.textContent = detailsParts.length > 0 ? detailsParts.join(" • ") : (detailsEl.textContent || "—");
    }
  }

  renderPreview(data) {
    this.updateSelectedMemberFromPreview(data);

    const container = this.element.querySelector("[data-bulk-match-section]");
    const singleLabel = this.element.querySelector("[data-bulk-match-single]");
    const bulkLabel = this.element.querySelector("[data-bulk-match-bulk]");
    const bulkLabelParent = bulkLabel?.closest("label");
    const bulkRadio = this.element.querySelector('input[name="apply_mode"][value="bulk"]');
    const singleRadio = this.element.querySelector('input[name="apply_mode"][value="single"]');
    const helpText = this.element.querySelector("[data-bulk-match-help]");
    const memberRefText = this.element.querySelector("[data-bulk-match-member-ref]");
    if (!container) return;

    const total = data.total_matching || 0;
    const willUpdate = data.will_update ?? 0;
    const skipped = data.skipped ?? 0;
    const recordType = data.record_type || "attendance";
    const memberName = data.selected_member_name || "";

    // Show bulk option only when 2+ rows would actually be updated
    if (willUpdate <= 1) {
      if (bulkLabelParent) bulkLabelParent.classList.add("hidden");
      if (bulkRadio) bulkRadio.checked = false;
      if (singleRadio) singleRadio.checked = true;
      if (helpText) {
        helpText.classList.add("hidden");
        helpText.textContent = "";
      }
      if (memberRefText) {
        memberRefText.classList.add("hidden");
        memberRefText.textContent = "";
      }
      return;
    }

    if (bulkLabelParent) bulkLabelParent.classList.remove("hidden");
    if (bulkLabel) bulkLabel.textContent = `Update all ${willUpdate} matching ${recordType} records in this batch`;

    if (memberName) {
      const refSpan = this.element.querySelector("[data-bulk-match-member-ref]");
      if (refSpan) {
        refSpan.classList.remove("hidden");
        refSpan.textContent = "";
        refSpan.appendChild(document.createTextNode("and assign them to "));
        const strong = document.createElement("strong");
        strong.textContent = memberName;
        refSpan.appendChild(strong);
      }
    }

    if (helpText) {
      helpText.classList.remove("hidden");
      let msg = "";
      if (memberName) {
        msg = `This will assign the selected member ${memberName} to all currently unmatched ${recordType} rows with the same normalized identity in this batch.`;
      } else if (skipped > 0) {
        msg = `${willUpdate} unmatched records will be updated. ${skipped} already matched records will be skipped.`;
      } else {
        msg = `This will assign the selected staged member to all currently unmatched ${recordType} rows with the same normalized identity in this batch.`;
      }
      if (skipped > 0 && memberName) {
        msg += ` ${skipped} already matched records will be skipped.`;
      }
      helpText.textContent = msg;
    }
  }
}
